const bayer = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5]

export function ditherPoints(tone: { width: number; height: number; rows: string[] }) {
  const points: { x: number; y: number }[] = []
  for (let y = 0; y < tone.height; y++) {
    for (let x = 0; x < tone.width; x++) {
      const density = parseInt(tone.rows[y][x], 16) / 15
      if (density > (bayer[(y % 4) * 4 + (x % 4)] + 0.5) / 16) points.push({ x, y })
    }
  }
  return points
}

export function ditherClusters(tone: Parameters<typeof ditherPoints>[0]) {
  const clusters = new Map<number, { x: number; y: number; offsets: { x: number; y: number }[] }>()
  const columns = Math.ceil(tone.width / 3)
  for (const point of ditherPoints(tone)) {
    const key = Math.floor(point.y / 3) * columns + Math.floor(point.x / 3)
    const cluster = clusters.get(key)
    if (cluster) cluster.offsets.push({ x: point.x - cluster.x, y: point.y - cluster.y })
    else clusters.set(key, { ...point, offsets: [] })
  }
  return [...clusters.values()]
}

export function repulsionOffset(dx: number, dy: number, radius: number) {
  const distance = Math.hypot(dx, dy)
  if (distance >= radius) return { x: 0, y: 0 }
  const force = 65 * (1 - distance / radius) ** 2
  return distance < 0.001
    ? { x: force, y: 0 }
    : { x: (dx / distance) * force, y: (dy / distance) * force }
}

export function springStep(
  state: { x: number; y: number; vx: number; vy: number },
  target: { x: number; y: number },
  delta: number,
) {
  let { x, y, vx, vy } = state
  const elapsed = Math.min(Math.max(delta, 0), 1 / 30)
  const steps = Math.max(1, Math.ceil(elapsed * 120))
  const dt = elapsed / steps
  for (let step = 0; step < steps; step++) {
    vx += ((target.x - x) * 260 - vx * 19) * dt
    vy += ((target.y - y) * 260 - vy * 19) * dt
    x += vx * dt
    y += vy * dt
  }
  if (Math.abs(target.x - x) + Math.abs(target.y - y) + Math.abs(vx) + Math.abs(vy) < 0.06)
    return { x: target.x, y: target.y, vx: 0, vy: 0 }
  return { x, y, vx, vy }
}

/**
 * Returns a function that maps a normalized time t (0–1) to an eased
 * progress value (0–1) along a cubic bezier curve defined by the two
 * control points (x1,y1) and (x2,y2). The curve starts at (0,0) and
 * ends at (1,1).
 */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number) {
  return (t: number) => {
    // Newton-Raphson to find the bezier x parameter for time t.
    let u = t
    for (let i = 0; i < 8; i++) {
      const x = 3 * (1 - u) ** 2 * u * x1 + 3 * (1 - u) * u ** 2 * x2 + u ** 3
      const dx = 3 * (1 - u) ** 2 * x1 + 6 * (1 - u) * u * (x2 - x1) + 3 * u ** 2 * (1 - x2)
      if (Math.abs(x - t) < 0.001) break
      u += (t - x) / dx
    }
    return 3 * (1 - u) ** 2 * u * y1 + 3 * (1 - u) * u ** 2 * y2 + u ** 3
  }
}

let webglSupport: boolean | undefined

/**
 * Whether the client can create a WebGL context. Cached — the result
 * cannot change during a session, and probing allocates a context.
 */
export function supportsWebGL(): boolean {
  if (webglSupport !== undefined) return webglSupport
  if (typeof document === 'undefined') return (webglSupport = false)
  try {
    const canvas = document.createElement('canvas')
    webglSupport = !!(canvas.getContext('webgl2') ?? canvas.getContext('webgl'))
  } catch {
    webglSupport = false
  }
  return webglSupport
}
